/**
 * One data row: label, value with unit, status (icon and colour), the source
 * badge, freshness when the reading carries a time, a 500 ms flash when the
 * value changes, and the chart affordance when history exists.
 */
import { ChartLine } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ReadingSource, StatusColour } from '../../types';
import { useFlash } from '../lib/flash';
import { Freshness, SourceBadge, StatusIcon } from './badges';

export interface DataValueProps {
  label: string;
  value: string;
  unit?: string;
  colour: StatusColour | null;
  source: ReadingSource;
  /** Age of the value in ms; omitted for static JSON values. */
  ageMs?: number;
  freshMs?: number;
  description?: string;
  highlighted?: boolean;
  onChart?: () => void;
  className?: string;
}

export function DataValue({ label, value, unit, colour, source, ageMs, freshMs, description, highlighted, onChart, className }: DataValueProps) {
  const flashing = useFlash(value);
  return (
    <div
      title={description}
      className={cn(
        'flex items-center gap-2 px-2 py-1 text-xs transition-colors duration-500',
        flashing && 'bg-accent-soft',
        highlighted && 'bg-overlay',
        className,
      )}
    >
      <StatusIcon colour={colour} />
      <span className="min-w-0 flex-1 truncate text-ink-2">{label}</span>
      <span className={cn('font-mono tabular text-ink', colour === 'red' && 'text-warning', colour === 'yellow' && 'text-caution')}>
        {value}
        {unit && !value.endsWith(unit) ? <span className="ml-0.5 text-ink-3">{unit}</span> : null}
      </span>
      <SourceBadge source={source} />
      {ageMs !== undefined && <Freshness ageMs={ageMs} freshMs={freshMs} />}
      {onChart && (
        <button
          type="button"
          aria-label={`History of ${label}`}
          title="Readings and graph"
          onClick={(e) => {
            e.stopPropagation();
            onChart();
          }}
          className="rounded-control p-0.5 text-ink-3 hover:bg-overlay hover:text-accent"
        >
          <ChartLine className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
